/**
 * React error boundary for render-time exceptions (REQ-UI-191).
 *
 * React 18 offers boundaries only as class components, and the project
 * adds no dependency for a thirty-line class (CLAUDE.md §4.3), so this is
 * the one class component in the UI. It catches whatever its subtree throws
 * while rendering, in a lifecycle method, or in a constructor — effects
 * included, which is how the 2026-10-07 MapLibre ``getStyle()`` crash
 * surfaced — and renders :class:`ErrorFallback` in the subtree's place.
 *
 * Two scopes are mounted today:
 *
 * - ``panel`` — around each dockview panel component in
 *   :file:`multi-pane/panel-registry.tsx`. The fallback fills that panel's
 *   content area; the top bar, the other panels and the SSE providers above
 *   the workspace keep running. "Reload panel" remounts the subtree by
 *   bumping a key, so a transient failure (a removed map, a mid-flight
 *   layout reset) clears without a page load.
 * - ``page`` — around the whole provider tree in :file:`main.tsx`. Without
 *   it a crash in a provider unmounts the root and leaves a blank document;
 *   the fallback offers the only sensible action, a page reload.
 *
 * Route-level containment uses react-router's own ``errorElement`` instead
 * (:file:`chrome/route-error-fallback.tsx`), because the router catches
 * route-element errors before any ancestor boundary sees them.
 *
 * Cross-references: REQ-UI-191; REQ-UI-037; CLAUDE.md §8.1.
 */
import { Component, Fragment, type ErrorInfo, type ReactNode } from "react";
import { describeError } from "@/chrome/describe-error";
import { ErrorFallback, type ErrorFallbackScope } from "@/chrome/error-fallback";

export interface ErrorBoundaryProps {
  /** ``panel`` offers a remount; ``page`` offers only a reload. */
  scope: Extract<ErrorFallbackScope, "panel" | "page">;
  /** The surface in operator words — see :class:`ErrorFallback`. */
  surface: string;
  children: ReactNode;
  /** Optional observer — receives the thrown value and the surface name. */
  onError?: (error: unknown, surface: string, info: ErrorInfo) => void;
  /** Injectable page reload (defaults to ``window.location.reload``). */
  reloadPage?: () => void;
  /** Optional ``data-testid`` for the fallback root. */
  testId?: string;
}

interface ErrorBoundaryState {
  error: unknown;
  hasError: boolean;
  /** Bumped by "Reload panel" so the children remount from scratch. */
  attempt: number;
}

function defaultReload(): void {
  window.location.reload();
}

/**
 * Catch render errors beneath this node and show the failure card.
 *
 * See the module docstring for the two scopes and why the router has its
 * own path.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: undefined, hasError: false, attempt: 0 };

  static getDerivedStateFromError(error: unknown): Partial<ErrorBoundaryState> {
    return { error, hasError: true };
  }

  componentDidCatch(error: unknown, info: ErrorInfo): void {
    // React already reports the error itself to the console (in both dev
    // and production builds); the boundary adds the surface name so the
    // console line names what the operator saw, then hands off to the
    // optional observer. This is the only diagnostic sink the UI has —
    // there is no UI→BFF log shipping (REQ-UI-035).
    console.error(
      `[meridian-ui] render error contained in ${this.props.surface} (${this.props.scope}): ${describeError(error).summary}`,
    );
    this.props.onError?.(error, this.props.surface, info);
  }

  private readonly retry = (): void => {
    this.setState((prev) => ({ error: undefined, hasError: false, attempt: prev.attempt + 1 }));
  };

  render(): ReactNode {
    const { scope, surface, children, reloadPage, testId } = this.props;
    const reload = reloadPage ?? defaultReload;
    if (this.state.hasError) {
      if (scope === "panel") {
        return (
          <ErrorFallback
            scope="panel"
            surface={surface}
            error={this.state.error}
            primaryAction={{ label: "Reload panel", onClick: this.retry }}
            secondaryAction={{ label: "Reload page", onClick: reload }}
            testId={testId}
          />
        );
      }
      return (
        <ErrorFallback
          scope="page"
          surface={surface}
          error={this.state.error}
          primaryAction={{ label: "Reload page", onClick: reload }}
          testId={testId}
        />
      );
    }
    // A keyed fragment is how "Reload panel" remounts the subtree: a new
    // key discards the crashed instances and their state, and a Fragment
    // adds no DOM so panel sizing (``h-full`` off the dockview content
    // area) is untouched.
    return <Fragment key={this.state.attempt}>{children}</Fragment>;
  }
}
